import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Calendar, Users, ShoppingBag, MessageSquare, Star, MapPin, Clock, ArrowRight, Music, Headphones, Mic } from 'lucide-react'
import HeroSection from '../components/home/HeroSection'
import EventsRail from '../components/home/EventsRail'
import FeaturedArtists from '../components/home/FeaturedArtists'
import MarketplacePreview from '../components/home/MarketplacePreview'
import ReviewsSection from '../components/home/ReviewsSection'
import StatsSection from '../components/home/StatsSection'

const HomePage = () => {
  const features = [
    {
      icon: Calendar,
      title: 'Event Calendar',
      description: 'Never miss a show with our comprehensive calendar of local gigs, open mics, and festivals.',
      link: '/calendar',
      color: 'from-le-accent to-purple-600',
    },
    {
      icon: Users,
      title: 'Artist Profiles',
      description: 'Discover local talent, book shows, and connect with musicians in your area.',
      link: '/artists',
      color: 'from-le-accent-2 to-blue-600',
    },
    {
      icon: ShoppingBag,
      title: 'Gear Marketplace',
      description: 'Buy and sell music equipment with trusted local musicians and audio professionals.',
      link: '/marketplace',
      color: 'from-le-amber to-orange-600',
    },
    {
      icon: MessageSquare,
      title: 'Community Forums',
      description: 'Join discussions about gear, venues, techniques, and everything music-related.',
      link: '/forums',
      color: 'from-green-500 to-emerald-600',
    },
  ]

  return (
    <div className="pt-16 lg:pt-20">
      <HeroSection />
      
      {/* Features Grid */}
      <section className="section-padding py-20">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-display font-bold text-white mb-6">
              Everything You Need for
              <span className="text-gradient block">Wichita Music</span>
            </h2>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              From discovering tonight's hottest shows to connecting with fellow musicians and finding the perfect gear.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link
                    to={feature.link}
                    className="group glass-card p-8 block hover:bg-white/10 transition-all duration-300 hover:scale-105"
                  >
                    <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      <Icon size={32} className="text-white" />
                    </div>
                    <h3 className="text-2xl font-display font-semibold text-white mb-4 group-hover:text-le-accent transition-colors duration-300">
                      {feature.title}
                    </h3>
                    <p className="text-white/70 mb-6 leading-relaxed">
                      {feature.description}
                    </p>
                    <div className="flex items-center text-le-accent group-hover:text-le-accent-2 transition-colors duration-300">
                      <span className="font-medium">Explore</span>
                      <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                    </div>
                  </Link>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      <EventsRail />
      <FeaturedArtists />
      <MarketplacePreview />
      <ReviewsSection />
      <StatsSection />

      {/* CTA Section */}
      <section className="section-padding py-20">
        <div className="container-max">
          <motion.div
            className="glass-card p-12 text-center relative overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-le-accent/10 to-le-accent-2/10"></div>
            <div className="relative z-10">
              <h2 className="text-4xl lg:text-5xl font-display font-bold text-white mb-6">
                Ready to Join the
                <span className="text-gradient block">Wichita Music Scene?</span>
              </h2>
              <p className="text-xl text-white/70 mb-8 max-w-2xl mx-auto">
                Create your profile, discover events, connect with artists, and become part of our growing community.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="btn-primary text-lg px-8 py-4">
                  Get Started Free
                </button>
                <button className="btn-secondary text-lg px-8 py-4">
                  Learn More
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default HomePage